import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addInProgressItemsToOrder, getAllowedPriceRestrictedOrderItemQuantity, getDefaultItemPrice, getLowestItemPrice, getOrderItemCountByItemId, getOrderItemPriceForPriceRestriction, isDiscountedPriceAvailableForSellableItem, isDiscountItemPrice, removeOrderItemsByItemId } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useOccContext } from '../../../context/occ/hooks/use-occ-context';
import { ItemModal } from '../../../subcomponents/item-modal/item-modal';
import { getFormattedPrice } from '../../../subcomponents/item-picker/utils/get-display-price';
import { PromotedTag, PromotedTagAlignment } from '../../../subcomponents/promoted-tag';
import { getPromotionLabelForSellableItem } from '../../../utils/items/get-promotion-label';
import { SingleItemPickerContextInstance } from '../types/context';
import { getAllowedMaxQuantityForItem } from '../utils/get-allowed-max-quantity-for-item';
import { getPrice } from '../utils/item-price-utils';
import { SingleItemPickerItemDetails } from './item-details';
const b = bemClassBuilder('v-single-item-picker-item');
export const SingleItemPickerItem = observer(props => {
    const { sellableItem, priceRestriction } = props;
    const { i18n, displayText: occDisplayText } = useOccContext();
    const { displayText, config, assets, events, cdnOptions } = useContext(SingleItemPickerContextInstance);
    const { onItemModalOpen } = events;
    const [showModal, setShowModal] = useState(false);
    const handleModalOpen = useCallback(() => {
        onItemModalOpen === null || onItemModalOpen === void 0 ? void 0 : onItemModalOpen();
        setShowModal(true);
    }, [onItemModalOpen]);
    const handleModalClose = useCallback(() => {
        setShowModal(false);
    }, []);
    const handleClick = useCallback(() => {
        handleModalOpen();
    }, [handleModalOpen]);
    const handleAddToCart = () => {
        removeOrderItemsByItemId(sellableItem.itemId, priceRestriction);
        addInProgressItemsToOrder();
        handleModalClose();
    };
    const maximum = getAllowedMaxQuantityForItem(config.quantityLimit, sellableItem, priceRestriction);
    const quantityLimit = Object.assign(Object.assign({}, config.quantityLimit), { isUnlimited: false, maximum });
    const itemPriceForPriceRestrictedOrderItem = priceRestriction
        ? getOrderItemPriceForPriceRestriction(sellableItem, priceRestriction)
        : undefined;
    const promotionLabel = priceRestriction
        ? getPromotionLabelForSellableItem(sellableItem, occDisplayText, i18n, itemPriceForPriceRestrictedOrderItem)
        : undefined;
    const itemPrice = getItemPrice(sellableItem, priceRestriction, itemPriceForPriceRestrictedOrderItem);
    const hasDiscount = itemPrice
        ? isDiscountItemPrice(itemPrice)
        : isDiscountedPriceAvailableForSellableItem(sellableItem);
    const getDisplayPrice = useMemo(() => {
        return (priceFormat, itemQuantity, priceTemplate) => {
            var _a;
            return getFormattedPrice(priceTemplate || occDisplayText.price.itemPriceFormat, occDisplayText.price.discountedPriceFormat, {
                applyDiscountedFormat: (_a = priceFormat === null || priceFormat === void 0 ? void 0 : priceFormat.applyDiscountedFormat) !== null && _a !== void 0 ? _a : hasDiscount,
                showDefaultPrice: false
            }, getPrice(sellableItem, priceRestriction), i18n, itemQuantity);
        };
    }, [hasDiscount, sellableItem, priceRestriction, occDisplayText, i18n]);
    const orderItemCount = getOrderItemCountByItemId(sellableItem.itemId, priceRestriction);
    const allowedMaximumForPriceRestrictedItem = getAllowedPriceRestrictedOrderItemQuantity(maximum, sellableItem.itemId, true, itemPrice);
    return (_jsxs("li", Object.assign({ className: b.block() }, { children: [_jsx(SingleItemPickerItemDetails, { sellableItem: sellableItem, handleActionClick: handleClick, getDisplayPrice: getDisplayPrice, itemPrice: itemPrice, orderItemCount: orderItemCount }), _jsx(ItemModal, { readonly: false, sellableItem: sellableItem, show: showModal, className: b.element('modal'), onClose: handleModalClose, onAddToCart: handleAddToCart, displayText: displayText, assets: assets, config: Object.assign(Object.assign({}, config), { quantityLimit }), imageCdnOptions: cdnOptions.itemImage, getDisplayPrice: getDisplayPrice, promotionLabel: promotionLabel, isEditViewActive: !!orderItemCount, allowedMaximumForPriceRestrictedItem: allowedMaximumForPriceRestrictedItem, itemPrice: itemPrice }), promotionLabel && (_jsx(PromotedTag, { alignment: PromotedTagAlignment.Left, label: promotionLabel, className: b.element('promoted-tag') }))] })));
});
SingleItemPickerItem.displayName = 'SingleItemPickerItem';
const getItemPrice = (sellableItem, priceRestriction, priceRestrictedOrderItemPrice) => {
    if (!priceRestriction || (!priceRestriction.discountId && !priceRestriction.rewardId)) {
        return getDefaultItemPrice(sellableItem.prices);
    }
    if (!priceRestrictedOrderItemPrice)
        return getLowestItemPrice(sellableItem.prices);
    return priceRestrictedOrderItemPrice;
};
